import * as React from "react";
import * as Autocomplete from "react-autocomplete";
import { GameTokenUtil, IToken, CardType, GameToken, ICard, ICardReference } from "./Card";
import { IDragSource, Dnd, IDropTarget, IDrop } from "./Dnd";
import { TokenComponent } from "./Token";
import { CardToolbarComponent } from "./CardToolbarComponent";
import { ZoomedCardComponent } from "./ZoomedCardComponent";
import { IDeck, IDeckToolbarSettings, Deck } from "./Deck";
import { DeckUtil } from "./DeckUtil";
import { CardUtil } from "./CardUtil";
import { Stock } from "./Stock";

export interface IDeckProps {
    deck: IDeck;
    horizontal?: boolean;
    onChanged?: () => void;
    settings?: IDeckSettings;
}

export interface IDeckSettings {
    allowedTokens?: GameToken[];
    allowedDropCards?: string[];
    hideTokensOnBack?: boolean;
    hideTokensOnFront?: boolean;
    draggable?: boolean;
    toolbar?: IDeckToolbarSettings;
}
interface IDeckState {
    zoomed: boolean;
    shuffling: boolean;
    showAddCardSelector: boolean;
    nameOfCardToAdd: string;
    editDeck: boolean;
}
export class DeckComponent extends React.Component<IDeckProps, IDeckState> implements IDragSource, IDropTarget {
    deck: Deck;

    static dragCardTypeUnknown = "card/unknown";

    private div: HTMLDivElement;
    constructor(props: IDeckProps) {
        super(props);
        this.deck = new Deck(this.getSettings().toolbar);
        this.deckChanged = this.deckChanged.bind(this);

        this.state = { zoomed: false, shuffling: false, showAddCardSelector: false, nameOfCardToAdd: "", editDeck: false };
    }
    private getSettings(): IDeckSettings {
        let settings = this.props.settings;
        if (!settings) {
            settings = {};
        }
        if (!settings.toolbar) {
            settings.toolbar = {}
        }
        let toolbar = settings.toolbar;
        if (toolbar.flip === undefined)
            toolbar.flip = true;
        if (toolbar.zoom === undefined)
            toolbar.zoom = true;
        return settings;
    };
    onDragComplete(): void {
        DeckUtil.removeTopCard(this.props.deck);
    }

    getDragItem(): any {
        return this.deck.topCard();
    }
    getDropElement(): HTMLElement {
        return this.div;
    }
    allowDrop(dragType: string, effectAllowed: string): string {
        const settings = this.getSettings();

        if (/token\/(.+)/.test(dragType)) {
            const allowedTokens = settings.allowedTokens;
            if ((!allowedTokens || !allowedTokens.length))
                return null;
            const name = RegExp.$1;
            if (!name) return null;

            const gt = GameTokenUtil.parse(name);
            const matchingTokens = allowedTokens.filter(at => at === gt);
            if (matchingTokens && matchingTokens.length)
                return Dnd.move;
        }
        if (/card\/(.+)/.test(dragType)) {
            const allowedCards = settings.allowedDropCards;
            if ((!allowedCards || !allowedCards.length))
                return null;
            const name = RegExp.$1;
            if (!name) return null;

            //is wildcard present, or a matching type, allow
            if (allowedCards.filter(c => c === "*").length || allowedCards.filter(c => c === name))
                return Dnd.move;
        }

        return null;
    }
    flipAndZoomTopCard() {
        const card = this.deck.topCard();
        if (!card) return;
        if (!card.faceUp) {
            card.faceUp = true;
            this.props.onChanged();
        }
        this.setState({ zoomed: true });
    }
    onDrop(drop: IDrop): boolean {

        if (/token\/(.+)/.test(drop.dragType)) {
            const name = RegExp.$1;
            if (!name) return false;

            const gt = GameTokenUtil.parse(name);

            const card = this.deck.topCard();
            if (!card.tokens) {
                card.tokens = [];
            }
            let token: IToken;
            const matchingTokens = card.tokens.filter(t => t.token === gt);
            if (!matchingTokens.length) {
                token = { token: gt, number: 0 };
                card.tokens.push(token);
            } else {
                token = matchingTokens[0];
            }
            token.x = drop.targetX;
            token.y = drop.targetY;
            token.canBeMoved = true;
            token.number++;
            this.onChanged();
            return true;
        }
        if (/card\/(.+)/.test(drop.dragType)) {
            let card = drop.dragSource.getDragItem() as ICard;
            if (!card) return false;
            DeckUtil.addToTop(this.props.deck, card);
            this.onChanged();
            return true;
        }
        return false;

    }
    onChanged() {
        if (this.props.onChanged)
            this.props.onChanged();
    }
    deckChanged(s: string) {
        if (s === Deck.onChangeShuffleStart) {
            this.setState({ shuffling: true });
        } else if (s === Deck.onChangeShuffleEnd) {
            this.setState({ shuffling: false });
        } else if (s === Deck.onChangeToggleZoom) {
            this.setState({ zoomed: !this.state.zoomed });
        } else if (s === Deck.onShowAddCardSelector) {
            this.setState({ showAddCardSelector: true });
        } else if (s === Deck.onEdit) {
            this.setState({ editDeck: true });
        } else {
            this.onChanged();
        }
    }
    componentWillMount(): void {

        this.deck.onChange(this.deckChanged);
    }

    componentWillUnmount(): void {
        this.deck.removeOnChange(this.deckChanged);
    }
    addCardToDeck(card: ICard) {
        DeckUtil.addToTop(this.props.deck, card);
        this.setState({ nameOfCardToAdd: "", showAddCardSelector: false });
        this.props.onChanged();
    }
    filterCard(search: string, card: ICard): boolean {
        if (search === "" || search === null || search === undefined) return true;
        if (/trait\s*=\s*(\w+)/.test(search)) {
            const trait = RegExp.$1;
            const r = new RegExp(`\\b${trait}`, "i");
            return (r.test(card.subType2));
        }
        return CardUtil.getFullDisplayName(card).toLowerCase().indexOf(search.toLowerCase()) > -1;
    }
    render() {
        this.deck.setDeck(this.props.deck);
        let baseCss = "deck";
        if (this.props.horizontal)
            baseCss += "-horizontal";
        let css = baseCss;
        if (this.state.shuffling) {
            css += " shuffling";
        }

        const topCard = this.deck.topCard();
        if (topCard) {
            css += " " + CardUtil.getCardCss(topCard);
        }

        let draggable = false;
        let dragType: string = null;
        const settings = this.getSettings();
        if (settings.draggable && topCard) {
            draggable = true;
            if (topCard.faceUp) {
                dragType = "card/" + CardType[topCard.type].toLowerCase();
            } else {
                dragType = DeckComponent.dragCardTypeUnknown;
            }
        }
        let tokens: JSX.Element[] = null;

        if (topCard) {
            const hideTokens = ((topCard.faceUp && settings.hideTokensOnFront) || (!topCard.faceUp && settings.hideTokensOnBack));
            if (!hideTokens && topCard.tokens && topCard.tokens.length) {
                tokens = topCard.tokens.filter(t => t.number > 0).map((token) => {
                    token.canBeMoved = true;
                    return (<TokenComponent key={token.token} token={token} valueChanged={(no) => {
                        token.number += no;
                        this.onChanged();
                    }} />);
                });
            }
        }
        if (this.state.editDeck) {
            return (<div>
                <EditDeckComponent deck={this.props.deck} onClose={() => {
                    this.setState({ editDeck: false });
                    this.props.onChanged();
                }} />
            </div>);
        }
        if (this.state.showAddCardSelector) {
            const cards = Stock.getAllCards(!(!this.props.horizontal));
            const cardName = this.state.nameOfCardToAdd;
            css = baseCss;
            if (/^AHC(\d+):(\d+)/.test(cardName)) {
                var cr: ICardReference = { pack: parseInt(RegExp.$1), packNumber: parseInt(RegExp.$2) };
                css += ` ${CardUtil.getCardReferenceCss(cr, true)}`;
            }
            return (<div className={css} ref={(ref) => this.div = ref}>
                <div className="card-selector">
                    <Autocomplete items={cards}
                        shouldItemRender={(card, value) => this.filterCard(value, card)}
                        value={this.state.nameOfCardToAdd}
                        getItemValue={card => CardUtil.getFullDisplayName(card)}
                        renderItem={(card, highlighted) =>
                            <div
                                key={CardUtil.getKey(card)}
                                style={{ backgroundColor: highlighted ? '#eee' : 'transparent' }}
                            >
                                {CardUtil.getFullDisplayName(card)}
                            </div>}
                        onChange={e => this.setState({ nameOfCardToAdd: e.target.value })}
                        onSelect={(value, item) => this.addCardToDeck(item)}
                    />
                </div>
            </div>);
        }
        return (
            <div className={css} draggable={draggable} ref={(ref) => this.div = ref}
                onDragStart={(e) => { Dnd.dragStart(e, this, dragType, Dnd.move); }}
                onDragOver={(e) => { Dnd.dragOver(e, this); }}
                onDrop={(e) => { Dnd.drop(e, this); }}
            >
                <CardToolbarComponent deck={this.deck} />

                {tokens}
                {this.state.zoomed && this.deck.hasCard()
                    ? <ZoomedCardComponent deck={this.deck} horizontal={this.props.horizontal} />
                    : null}
            </div>
        );
    }


}

export interface IEditDeckProps {
    deck: IDeck;
    onClose: () => void;
}

interface IEditDeckState {
    decks: IDeck[];
}
export class EditDeckComponent extends React.Component<IEditDeckProps, IEditDeckState> implements IDropTarget {
    constructor(props: IEditDeckProps) {
        super(props);
        const deckCopy = ({ ...this.props.deck });
        this.state = { decks: [deckCopy] };
    }
    allowDrop(dragType: string, effectAllowed: string): string {
        if (!/card\/(.+)/.test(dragType))
            return null;
        return Dnd.move;
    }
    onDrop(drop: IDrop): boolean {
        const card = drop.dragSource.getDragItem() as ICard;
        if (!card) return false;
        const decks = this.state.decks;
        for (let i = 0; i < decks.length; i++) {
            if (decks[i].cards.length) continue;
            decks.splice(i, 1);
            i--;
        }
        decks.push({ cards: [card] });
        this.setState({ decks: decks });
        return true;
    }
    private div: HTMLDivElement;

    componentDidMount(): void {
        this.div.focus();
    }
    saveBackToProps() {
        const mainDeck = this.state.decks[0];
        for (let i = 1; i < this.state.decks.length; i++) {
            const deck = this.state.decks[i];
            for (let j = 0; j < deck.cards.length; j++) {
                DeckUtil.addToTop(mainDeck, deck.cards[j]);
            }
        }
        CardUtil.flip(mainDeck.cards, false);
        this.props.deck.cards = mainDeck.cards;

    }
    onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
        switch (e.key) {
            case "Escape":
                this.saveBackToProps();
                this.props.onClose();
                e.preventDefault();
                e.stopPropagation();
                return true;
        }
    }

    render() {
        var settings: IDeckSettings = { draggable: true, allowedDropCards: ["*"] };
        const deckComponents: JSX.Element[] = this.state.decks.filter(d => d.cards.length).map((d, i) => { return (<DeckComponent key={i} deck={d} settings={settings} onChanged={() => this.forceUpdate()} />); });
        return (
            <div className="backdrop" tabIndex={1} onKeyDown={(e) => this.onKeyDown(e)} ref={(ref) => this.div = ref}>
                <div id="edit-deck">
                    <div className="content-box" onDragOver={(e) => { Dnd.dragOver(e, this); }}
                        onDrop={(e) => { Dnd.drop(e, this); }} >
                        {deckComponents}
                    </div>
                </div>
            </div>
        );
    }
}
